import React from "react";
import { Gift } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip } from "recharts";
import { useLang } from "../i18n";
import { Reveal, Eyebrow, SectionShell } from "./Reveal";

const ASSET_COLORS = ["#38BDF8", "#7DD3FC", "#0EA5E9", "#0284C7", "#0369A1", "#6366F1", "#10B981", "#EAB308", "#1E293B"];
const ASSET_VALUES = [5, 2, 3, 10, 10, 10, 10, 10, 40];

const RAD = Math.PI / 180;
const renderPct = ({ cx, cy, midAngle, outerRadius, value, fill }) => {
  const r = outerRadius + 16;
  const x = cx + r * Math.cos(-midAngle * RAD);
  const y = cy + r * Math.sin(-midAngle * RAD);
  const anchor = x > cx + 2 ? "start" : x < cx - 2 ? "end" : "middle";
  return React.createElement(
    "text",
    {
      x,
      y,
      fill,
      textAnchor: anchor,
      dominantBaseline: "central",
      fontSize: 12,
      fontWeight: 700,
    },
    `${value}%`
  );
};

export default function Pioneers() {
  const { t } = useLang();

  return (
    <SectionShell id="pionieri" testid="pioneers-presale-section">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 h-[420px] w-[820px] rounded-full bg-sky-200/40 blur-[140px]" />
      </div>

      <Reveal className="text-center">
        <Eyebrow>{t.pioneers.eyebrow}</Eyebrow>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">
          {t.pioneers.title}
        </h2>
        <p className="mt-4 max-w-3xl text-slate-600 text-base sm:text-lg leading-relaxed">{t.pioneers.intro}</p>
        <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-4 py-2 font-mono text-xs sm:text-sm font-semibold text-amber-700">
          <Gift size={15} />
          {t.pioneers.supplyBadge}
        </div>
      </Reveal>

      <Reveal delay={0.15} className="mt-8 text-center">
        <Eyebrow>{t.pioneers.howEyebrow}</Eyebrow>
        <h3 className="mt-3 font-display text-xl sm:text-2xl font-bold text-slate-900">{t.pioneers.howTitle}</h3>
        <div className="mx-auto mt-3 max-w-2xl space-y-2 text-slate-600 leading-relaxed">
          {t.pioneers.howLines.map((line, i) => (
            <p key={i}>{line}</p>
          ))}
        </div>
      </Reveal>

      <Reveal delay={0.1} className="mt-14 text-center">
        <h3 className="font-display text-xl sm:text-2xl font-bold text-slate-900">
          {t.pioneers.notesTitle}
        </h3>
        <ul className="mx-auto mt-6 flex max-w-3xl flex-col gap-4" data-testid="notes-list">
          {t.pioneers.notes.map((n, i) => (
            <li
              key={i}
              data-testid={`note-card-${i + 1}`}
              className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm text-sm text-slate-600 leading-relaxed"
            >
              <span className="shrink-0 rounded-lg bg-sky-50 px-2 py-1 font-mono text-xs font-bold text-sky-600">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span>{n}</span>
            </li>
          ))}
        </ul>

        <div
          className="mt-10 rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm"
          data-testid="asset-breakdown"
        >
          <h4 className="font-display text-lg font-bold text-slate-900">{t.pioneers.assetTitle}</h4>
          <p className="mx-auto mt-1 max-w-md text-sm text-slate-600">{t.pioneers.assetLead}</p>

          <div className="mt-6 grid sm:grid-cols-[300px_1fr] items-center gap-6 sm:gap-8">
            <div className="mx-auto" data-testid="asset-pie">
              <PieChart width={300} height={260}>
                <Pie
                  data={t.pioneers.assetNames.map((n, i) => ({
                    name: n,
                    value: ASSET_VALUES[i],
                    color: ASSET_COLORS[i],
                  }))}
                  cx={150}
                  cy={130}
                  dataKey="value"
                  innerRadius={52}
                  outerRadius={88}
                  paddingAngle={2}
                  stroke="none"
                  label={renderPct}
                  labelLine={false}
                >
                  {ASSET_COLORS.map((c, i) => (
                    <Cell key={i} fill={c} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => `${v}%`} />
              </PieChart>
            </div>

            <ul className="space-y-2.5">
              {t.pioneers.assetRows.map((r, i) => (
                <li key={i} className="flex gap-3 text-sm text-slate-600 leading-relaxed">
                  <span
                    className="mt-1.5 h-3 w-3 shrink-0 rounded-full"
                    style={{ backgroundColor: ASSET_COLORS[i] }}
                    data-testid={`asset-dot-${i + 1}`}
                  />
                  {r}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Reveal>
    </SectionShell>
  );
}
